import type { OrderData } from '../types/order'

export function getOrderStatus(order: OrderData | null | undefined): string {
  if (!order) return ''
  return String(order.Status || order.currentStatus || '').trim()
}

export function isCeremonyOrder(order: OrderData | null | undefined): boolean {
  if (!order) return false
  return order.OrderType === 'ceremony' || String(order.OrderID || '').toUpperCase().startsWith('CO')
}

export function isPaymentCOD(order: OrderData | null | undefined): boolean {
  if (!order || isCeremonyOrder(order)) return false
  if (order.isCOD === true) return true
  return String(order.PaymentMethod || '').trim().toUpperCase() === 'COD'
}

/** Paid = ชำระเงินสำเร็จ หรือ สร้างออร์เดอร์ + PaidAt */
export function isBeamPaid(order: OrderData | null | undefined): boolean {
  if (!order) return false
  const st = getOrderStatus(order)
  if (st === 'ชำระเงินสำเร็จ') return true
  if (st === 'สร้างออร์เดอร์' && order.PaidAt) return true
  return false
}

/** ฟอร์มยืนยันเปิดได้เมื่อยังไม่ส่ง หรือแอดมินย้อนสถานะกลับมา */
export function isOrderFormEditable(order: OrderData | null | undefined): boolean {
  if (!order) return false
  const st = getOrderStatus(order)
  if (st === 'สร้างออร์เดอร์' || st === 'ชำระเงินสำเร็จ') return true
  if (order.PaidAt && !order.CustomerConfirmedAt) return true
  return false
}

export function isOrderSubmitted(order: OrderData | null | undefined): boolean {
  if (!order) return false
  // แอดมินอาจย้อนสถานะเป็น สร้างออร์เดอร์ แต่ยังมี CustomerConfirmedAt ค้าง — ต้องเปิดฟอร์มยืนยันใหม่
  if (isOrderFormEditable(order)) return false
  const st = getOrderStatus(order)
  if (order.CustomerConfirmedAt) return true
  // 'รอประกอบพิธี' / 'เสร็จพิธี' = สถานะเฉพาะออร์เดอร์พิธี (CO) แทน 'รอจัดส่ง' / 'สำเร็จ'
  return ['รอตรวจสอบ', 'รอจัดส่ง', 'สำเร็จ', 'จัดส่งแล้ว', 'รอประกอบพิธี', 'เสร็จพิธี'].includes(st)
}

/**
 * สถานะที่ "มีผลต่อการ poll" ของออร์เดอร์ — effect หลักผูกกับค่านี้แทนอ็อบเจ็กต์ order ทั้งก้อน
 * เพราะ watchOrder สร้าง order ใหม่ทุก snapshot ถ้าผูกกับ order ทุกครั้งที่ออร์เดอร์เปลี่ยน
 * startPoll จะรีเซ็ต backoff กลับไปทุก 3 วิ (เคสจริง ต.ค.–พ.ย. 2569: createBeamCharge 735k ครั้ง/14 วัน)
 */
export function beamPollState(order: OrderData | null): 'none' | 'paid' | 'hidden' | 'active' {
  if (!order) return 'none'
  if (isBeamPaid(order)) return 'paid'
  if (shouldHideBeamQr(order) || order.isCOD) return 'hidden'
  return 'active'
}

export function shouldHideBeamQr(order: OrderData | null | undefined): boolean {
  if (isBeamPaid(order)) return true
  return getOrderStatus(order) === 'รอตรวจสอบ'
}

export function resolveSubmitStatus(order: OrderData | null | undefined): string {
  if (isPaymentCOD(order) || isBeamPaid(order)) return 'รอจัดส่ง'
  return 'รอตรวจสอบ'
}

export function parseOrderTotal(order: OrderData | null | undefined): number {
  if (!order) return 0
  const raw = order.TotalPrice != null ? order.TotalPrice : order.Total
  if (raw == null || raw === '') return 0
  return parseInt(String(raw).replace(/[^\d]/g, ''), 10) || 0
}

const MAX_DISCOUNT_ITEMS = 20

function positiveAmount(v: unknown): number {
  const n = parseFloat(String(v ?? '').replace(/[^\d.-]/g, ''))
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * ส่วนลดรวมของออร์เดอร์ (บาท) — 0 = ไม่ต้องแสดงบรรทัดส่วนลด
 *
 * ค่านี้มีไว้ "บอกให้ลูกค้ารู้" เท่านั้น — TotalPrice หักส่วนลดไปแล้ว
 * ห้ามเอาไปลบจาก TotalPrice ซ้ำ และห้ามรวมราคาเองจาก Item{i}Price
 * (ฟิลด์นั้นเป็นยอดรวมบรรทัดในออร์เดอร์แอดมิน แต่เป็นราคาต่อชิ้นในออร์เดอร์ CF)
 *
 * ปกติ ordercus-firebase.js คำนวณ DiscountTotal มาให้แล้ว — ที่เหลือเป็น fallback
 * เผื่อ order object มาจาก boot cache รุ่นเก่าที่ยังมีฟิลด์ดิบติดมา
 */
export function getOrderDiscountTotal(order: OrderData | null | undefined): number {
  if (!order) return 0
  const precomputed = positiveAmount(order.DiscountTotal)
  if (precomputed > 0) return precomputed

  // แบบ B — ส่วนลดระดับออร์เดอร์ (CF/แชท) มาก่อน
  const raw = order as unknown as Record<string, unknown>
  const orderLevel = positiveAmount(raw.Discount)
  if (orderLevel > 0) return orderLevel

  // แบบ A — รวมส่วนลดรายชิ้น (แอดมินเปิดเอง) ต้องคูณจำนวน
  let sum = 0
  for (let i = 1; i <= MAX_DISCOUNT_ITEMS; i++) {
    if (!raw[`Item${i}Name`]) continue
    const perUnit = positiveAmount(raw[`Item${i}Discount`])
    if (!perUnit) continue
    const qty = Math.max(1, parseInt(String(raw[`Item${i}Qty`] ?? '1'), 10) || 1)
    sum += perUnit * qty
  }
  return sum
}

export function formatPayAmountTh(amount: number): string {
  return (Number(amount) || 0).toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function formatPaidAtTh(paidAt?: string | { toDate?: () => Date } | null): string {
  if (!paidAt) return ''
  try {
    const date =
      typeof paidAt === 'object' && paidAt.toDate
        ? paidAt.toDate()
        : new Date(String(paidAt))
    if (isNaN(date.getTime())) return ''
    return (
      'เมื่อ ' +
      date.toLocaleString('th-TH', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    )
  } catch {
    return ''
  }
}

export function flagIsTrue(val: string | undefined | null): boolean {
  const s = String(val || '').trim().toUpperCase()
  return s === 'TRUE' || s === 'YES' || s === '1'
}

export function getExistingSlipUrl(order: OrderData | null | undefined): string {
  if (!order) return ''
  const raw = String(order.SlipURL || '').trim()
  if (!raw || raw === 'Error Uploading Slip') return ''
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('//')) return raw
  return ''
}

export function isOrderCancelled(order: OrderData | null | undefined): boolean {
  if (!order) return false
  const st = getOrderStatus(order)
  return st === 'ยกเลิก' || st === 'canceled'
}

export function resolveOrderIdFromUrl(): string {
  if (window.__earlyOrderId) return window.__earlyOrderId
  try {
    const sp = new URLSearchParams(window.location.search)
    let oid = sp.get('orderId')
    if (!oid && sp.get('liff.state')) {
      let st = decodeURIComponent(sp.get('liff.state')!)
      if (st.startsWith('?')) st = st.substring(1)
      oid = new URLSearchParams(st).get('orderId')
    }
    if (!oid) {
      const m = window.location.href.match(/orderId=([^&]+)/)
      if (m) oid = decodeURIComponent(m[1])
    }
    if (!oid) {
      const m = window.location.href.match(/(CO\d{9,}|\d{9,})/)
      if (m) oid = m[1]
    }
    if (oid) return oid.replace(/[^a-zA-Z0-9\-]/g, '')
  } catch {
    /* ignore */
  }
  return ''
}

/**
 * ตรวจว่าออร์เดอร์นี้ถูกเปิดจากปุ่ม LIFF ของ LINE หรือไม่
 * Desktop LINE ส่งกลับมาที่โดเมนเว็บพร้อม liff.state แต่ไม่มี in-app user agent
 * จึงใช้เฉพาะเป็นสัญญาณเพื่อแสดงปุ่มทางเข้า “ออร์เดอร์ของฉัน”; สิทธิ์อ่านรายการยังตรวจด้วย ID token ที่ memberApi
 */
export function isLineOrderEntryUrl(orderId: string, href = window.location.href): boolean {
  const expected = String(orderId || '').replace(/[^a-zA-Z0-9-]/g, '')
  if (!expected) return false
  try {
    const url = new URL(href, 'https://order.srikanett.com')
    let state = url.searchParams.get('liff.state') || ''
    for (let attempt = 0; attempt < 2 && /%3[fd]/i.test(state); attempt += 1) {
      state = decodeURIComponent(state)
    }
    if (state.startsWith('?')) state = state.slice(1)
    const stateParams = new URLSearchParams(state)
    const incoming = String(stateParams.get('orderId') || '').replace(/[^a-zA-Z0-9-]/g, '')
    return stateParams.get('page') === 'customer-order' && incoming === expected
  } catch {
    return false
  }
}

export function resolveBotIdFromHref(href = window.location.href): string {
  const m = href.match(/botId=(\d+)/)
  if (m?.[1]) return m[1]
  if (href.includes('liff.state=')) {
    try {
      const stateRaw = href.split('liff.state=')[1].split('&')[0]
      const decoded = decodeURIComponent(decodeURIComponent(stateRaw))
      const bm = decoded.match(/botId=(\d+)/)
      if (bm?.[1]) return bm[1]
    } catch {
      /* ignore */
    }
  }
  if (href.includes('2009352737')) return '2'
  if (href.includes('2008655508')) return '1'
  return ''
}

export function inferBotIdFromLineChannel(ch?: string): string {
  const s = String(ch || '').trim().toLowerCase()
  if (!s) return ''
  if (/bot\s*2|ศรีคเนศ|เทวาลัย/i.test(s)) return '2'
  if (/bot\s*1|sriganett/i.test(s)) return '1'
  return ''
}

export function getOrderLineUserId(order: OrderData | null | undefined): string {
  if (!order) return ''
  return String(order.LineUserID || order.LastViewerID || '').trim()
}

export function sleepMs(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}
